import { hc } from "hono/client";
import type { AppType } from "../../../backend/src/client";
import config from "../constants/config";

// API Error class for handling backend errors
export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    public originalError: Error,
    // Full parsed JSON error body, when the backend returned one. Optional and
    // additive so existing call sites (status/code/originalError) are unchanged.
    // Lets callers recover structured fields the message-only extraction drops
    // (e.g. the deletion endpoint's `error` + `blockers`/`remaining` arrays).
    public body?: unknown
  ) {
    super(`API Error ${status}: ${code}`);
    this.name = "APIError";
  }
}

// Create the Hono RPC client
export const client = hc<AppType>(config.apiUrl, {
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      // eslint-disable-next-line custom/no-await-fetch
      const response = await fetch(input, {
        ...init,
        credentials: "include", // Include cookies for authentication
        headers: {
          "Content-Type": "application/json",
          ...init?.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        let errorCode = "UNKNOWN_ERROR";
        let errorBody: unknown;

        try {
          const errorData = await response.clone().json() as { message?: string; code?: string };
          errorBody = errorData;
          errorMessage = errorData.message || errorMessage;
          errorCode = errorData.code || errorCode;
        } catch {
          // Fallback to status text if JSON parsing fails
          errorMessage = response.statusText || errorMessage;
        }

        throw new APIError(response.status, errorCode, new Error(errorMessage), errorBody);
      }

      return response;
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }
      // Network errors or other fetch errors
      throw new APIError(0, "NETWORK_ERROR", error as Error);
    }
  },
});